/* eslint-disable max-lines */
import React, { Fragment } from 'react';
import { cn, jcm } from 'utils';
import { RegularButton } from 'fronton-react';
import type { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { VariantImage, VariantImageDataProps } from 'common/components/product-variants/variant-image';
import { VariantItem } from 'common/components/product-variants/variant-item';
import { getIsBot } from 'common/selectors';
import {
  VARIANT_ITEM_CLICK,
  VARIANT_MORE_BUTTON_CLICK,
} from 'common/components/product-variants/ducks/actions';
import { AxisPoint } from 'features/product-info/components/pdp-variants/types';
import * as styles from './styles';
import { VariantAxisProps } from './types';
import { adjustScroll, getVariantNumberToRender, getVariantNumberToRenderThreeRows } from './utils';
import { Variant } from './utils/types';

type VariantAxisState = {
  showedAxisLable?: string;
  showAllVariants?: boolean;
  extraVariant: number | null;
};

export class VariantAxis extends React.Component<VariantAxisProps, VariantAxisState> {
  scrollCntrRef: React.RefObject<HTMLDivElement>;

  activePointRef: React.RefObject<HTMLDivElement>;

  parentVariantsRef: React.RefObject<HTMLDivElement>;

  constructor(props: VariantAxisProps) {
    super(props);

    this.scrollCntrRef = React.createRef();
    this.activePointRef = React.createRef();
    this.parentVariantsRef = React.createRef();
    this.state = {
      showAllVariants: false,
      extraVariant: null,
    };
  }

  componentDidMount() {
    this.setState({
      extraVariant:
        (this.props.asImageThumb
          ? getVariantNumberToRender(this.scrollCntrRef)
          : getVariantNumberToRenderThreeRows(this.scrollCntrRef)) ?? this.props.axisPoints.length,
    });
    if (window?.innerWidth < 768 || this.props.variantAxisCount === 2) {
      this.setState({
        showAllVariants: true,
      });
    }
  }

  componentDidUpdate(prevProps: VariantAxisProps) {
    const { asOneLine, activePointKey } = this.props;
    const needUpdateScroll =
      (!!asOneLine && prevProps.asOneLine !== asOneLine) ||
      (!!asOneLine && prevProps.activePointKey !== activePointKey);

    if (needUpdateScroll) {
      adjustScroll(this.scrollCntrRef.current, this.activePointRef.current);
    }
  }

  render() {
    const {
      axisPoints,
      pointKeyGetter,
      activePointKey,
      axisTitle,
      axisPointLabel,
      asImageThumb,
      ProductLinkComponent,
      className = '',
      asOneLine,
      isBot,
      busy,
    } = this.props;

    const { showedAxisLable } = this.state;
    const setShowedAxisLable = (v?: string) => this.setState({ showedAxisLable: v });

    const poinsListClassesMap = {
      [styles.axisPointsList]: true,
      [styles.axisPointsListOfImages]: !!asImageThumb,
      [styles.axisPointsListOneLine]: !!asOneLine,
      unfolded: true,
    };
    const containerClasses = {
      [styles.variantAxis]: true,
      [className]: true,
    };

    const acceptButtonStyle = this.state.extraVariant !== this.props.axisPoints.length;

    const displayButton = acceptButtonStyle && !this.state.showAllVariants && !isBot;

    const extraVariant = this.state.extraVariant || 0;

    const extraVariantsToShow = axisPoints.length - extraVariant - 1;

    const moreVariantsBtnClickCb = () => {
      this.setState({ showAllVariants: true });
      this.props.onShowMoreButtonClick({ extraVariantsToShow, axisTitle });
    };

    const { scrollCntrRef, activePointRef, parentVariantsRef } = this;
    const { showAllVariants } = this.state;
    return (
      <div className={jcm(containerClasses)} data-qa="variant-axis">
        <div className={styles.axisTitle} data-qa="variant-title">
          {axisTitle}: <span className={styles.axisPointLabelStyles}>{showedAxisLable}</span>
        </div>
        <div
          className={cn(
            styles.pointsListWrapper,
            this.state.extraVariant !== null || isBot ? styles.showContainer : styles.hideContainer,
          )}
          ref={parentVariantsRef}
          data-qa="variant-points-list"
        >
          <div className={jcm(poinsListClassesMap)} ref={scrollCntrRef}>
            {axisPoints.map((ap, idx) => {
              const isPuncturedPoint = !!ap.isPuncturedPoint;
              const imageClasses = {
                [styles.imageOfPuncturePoint]: isPuncturedPoint,
              };
              const content = asImageThumb ? (
                <VariantImage className={jcm(imageClasses)} {...(ap as unknown as VariantImageDataProps)} />
              ) : (
                ap.label
              );
              const axisPointWrapperClassesMap = {
                [styles.axisPointWrapper]: true,
                label: true,
              };
              const hideElement =
                this.state.extraVariant !== null && idx > this.state.extraVariant && !isBot;
              const pointKey = pointKeyGetter(ap, idx);
              const isActive = pointKey === activePointKey;
              const itemProps: AxisPoint & {
                ref?: ((instance: HTMLDivElement | null) => void) | React.RefObject<HTMLDivElement> | null;
              } = { ...ap };
              if (isActive) {
                itemProps.ref = activePointRef;
              }
              const isVariantVisible = showAllVariants || !hideElement;

              return (
                <Fragment key={pointKey}>
                  {isVariantVisible && (
                    <div
                      data-qa="variant-element"
                      className={
                        !this.props.asOneLine
                          ? cn({
                              [styles.showAllVariants]: showAllVariants,
                              [styles.hideElement]: hideElement,
                            })
                          : undefined
                        // hides or shows variants depends on the width calculation
                      }
                    >
                      <div
                        className={jcm(axisPointWrapperClassesMap)}
                        onClick={() => this.props.onVariantClick(ap)}
                        onMouseEnter={() => setShowedAxisLable(ap.label.includes('http') ? '' : ap.label)}
                        onMouseLeave={() => setShowedAxisLable(axisPointLabel)}
                        data-qa="variant-axis-point"
                      >
                        <VariantItem
                          {...itemProps}
                          ProductLinkComponent={ProductLinkComponent}
                          asImageThumb={asImageThumb}
                          active={isActive}
                          disabled={busy}
                          inactive={isPuncturedPoint}
                          isDrawerVariant
                        >
                          {content}
                        </VariantItem>
                      </div>
                    </div>
                  )}
                </Fragment>
              );
            })}
            {displayButton && (
              <RegularButton
                className={acceptButtonStyle ? styles.pointListShowMoreBtn : styles.hideElement}
                variant="secondary"
                onClick={() => moreVariantsBtnClickCb()}
              >
                {`+${extraVariantsToShow}`}
              </RegularButton>
            )}
          </div>
        </div>
      </div>
    );
  }
}
const mapStoreToProps = (store: RootState) => ({
  isBot: getIsBot(store) || false,
});
const mapDispatchToProps = (dispatch: Dispatch) => ({
  onVariantClick: (payload: Variant) => dispatch(VARIANT_ITEM_CLICK(payload)),
  onShowMoreButtonClick: (payload: { extraVariantsToShow: number; axisTitle: string }) =>
    dispatch(VARIANT_MORE_BUTTON_CLICK(payload)),
});
export const PdpVariantAxis = connect(mapStoreToProps, mapDispatchToProps)(VariantAxis);
